/**
 * Tenant branding.
 *
 * A white-label partner overrides exactly two colours and the logo slot:
 * `--chrome-900` and `--brass`. Everything else is DERIVED here, in OKLCH, using the
 * lightness deltas measured off the stock palette — so a partner cannot pick a pair that
 * breaks contrast, and chrome-800/700/600 stay a coherent family whatever the hue.
 *
 * See docs/DESIGN_SYSTEM.md § White label.
 */

import { useSyncExternalStore } from 'react'
import type { PublicBranding } from '@anubis-connector/shared'
import { apiV1 } from './api'

/* ── sRGB ⇄ OKLCH ─────────────────────────────────────────────────────────── */

export interface Rgb {
  r: number
  g: number
  b: number
}
export interface Oklch {
  l: number
  c: number
  h: number
}

export const parseHex = (input: string): Rgb | null => {
  const raw = input.trim().replace(/^#/, '')
  const hex = raw.length === 3 ? raw.replace(/./g, (ch) => ch + ch) : raw
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null
  return {
    r: Number.parseInt(hex.slice(0, 2), 16) / 255,
    g: Number.parseInt(hex.slice(2, 4), 16) / 255,
    b: Number.parseInt(hex.slice(4, 6), 16) / 255,
  }
}

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n)

const toHexPair = (channel: number): string =>
  Math.round(clamp01(channel) * 255)
    .toString(16)
    .padStart(2, '0')

export const toHex = ({ r, g, b }: Rgb): string => `#${toHexPair(r)}${toHexPair(g)}${toHexPair(b)}`

const linearise = (channel: number): number =>
  channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4

const delinearise = (channel: number): number =>
  channel <= 0.0031308 ? channel * 12.92 : 1.055 * channel ** (1 / 2.4) - 0.055

export const rgbToOklch = (rgb: Rgb): Oklch => {
  const r = linearise(rgb.r)
  const g = linearise(rgb.g)
  const b = linearise(rgb.b)

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)

  const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s

  const chroma = Math.sqrt(okA * okA + okB * okB)
  const hue = chroma < 1e-6 ? 0 : (Math.atan2(okB, okA) * 180) / Math.PI
  return { l: okL, c: chroma, h: hue < 0 ? hue + 360 : hue }
}

const oklchToRgbRaw = ({ l: okL, c, h }: Oklch): Rgb => {
  const rad = (h * Math.PI) / 180
  const okA = c * Math.cos(rad)
  const okB = c * Math.sin(rad)

  const l = (okL + 0.3963377774 * okA + 0.2158037573 * okB) ** 3
  const m = (okL - 0.1055613458 * okA - 0.0638541728 * okB) ** 3
  const s = (okL - 0.0894841775 * okA - 1.291485548 * okB) ** 3

  return {
    r: delinearise(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: delinearise(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: delinearise(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  }
}

const inGamut = ({ r, g, b }: Rgb): boolean =>
  r >= -0.001 && r <= 1.001 && g >= -0.001 && g <= 1.001 && b >= -0.001 && b <= 1.001

/** Converts back to sRGB, reducing chroma until the colour actually fits the gamut. */
export const oklchToRgb = (colour: Oklch): Rgb => {
  let chroma = colour.c
  let candidate = oklchToRgbRaw({ ...colour, c: chroma })
  for (let i = 0; i < 24 && !inGamut(candidate); i += 1) {
    chroma *= 0.88
    candidate = oklchToRgbRaw({ ...colour, c: chroma })
  }
  return { r: clamp01(candidate.r), g: clamp01(candidate.g), b: clamp01(candidate.b) }
}

/** Shifts a colour's OKLCH lightness by `delta`, optionally rescaling chroma. */
export const shiftLightness = (hex: string, delta: number, chromaScale = 1): string => {
  const rgb = parseHex(hex)
  if (!rgb) return hex
  const base = rgbToOklch(rgb)
  return toHex(
    oklchToRgb({
      l: Math.min(0.995, Math.max(0.005, base.l + delta)),
      c: base.c * chromaScale,
      h: base.h,
    }),
  )
}

/** Rebuilds a colour at an absolute lightness, keeping the hue. */
export const atLightness = (hex: string, lightness: number, maxChroma = Infinity): string => {
  const rgb = parseHex(hex)
  if (!rgb) return hex
  const base = rgbToOklch(rgb)
  return toHex(oklchToRgb({ l: lightness, c: Math.min(base.c, maxChroma), h: base.h }))
}

/* ── contrast ─────────────────────────────────────────────────────────────── */

const relativeLuminance = ({ r, g, b }: Rgb): number =>
  0.2126 * linearise(r) + 0.7152 * linearise(g) + 0.0722 * linearise(b)

/** WCAG 2.1 contrast ratio between two hex colours. Returns 1 when either is unparseable. */
export const contrastRatio = (a: string, b: string): number => {
  const rgbA = parseHex(a)
  const rgbB = parseHex(b)
  if (!rgbA || !rgbB) return 1
  const lumA = relativeLuminance(rgbA)
  const lumB = relativeLuminance(rgbB)
  const [hi, lo] = lumA > lumB ? [lumA, lumB] : [lumB, lumA]
  return (hi + 0.05) / (lo + 0.05)
}

/**
 * The stock palette, mirrored from styles/tokens.css.
 *
 * This is the ONE place in TypeScript where a colour literal is allowed, and only because
 * the derivation maths needs numbers, not `var()` references: OKLCH conversion, contrast
 * checks and the inline-override reset all operate on values. Nothing here styles anything.
 */
export const STOCK = {
  chrome: '#16191e',
  accent: '#dda32a',
  ink: '#191c21',
  inkMute: '#5c636e',
  inkFaint: '#8b929b',
  chromeInk: '#e7e9ec',
  surface: '#ffffff',
  hair: '#dee0dc',
  up: '#2e9e6b',
  down: '#c9503c',
} as const

/**
 * The neutral series ramp, `--chart-1` … `--chart-6`.
 *
 * Here for the same reason as `STOCK`: Recharts interpolates stroke and fill values itself and
 * cannot be handed a `var()`, so `components/charts/theme.ts` reads the live custom properties
 * and needs a literal to fall back to when it runs before the stylesheet has applied.
 */
export const STOCK_CHART_RAMP = [
  '#3d4756',
  '#6b7688',
  '#97a1b0',
  '#b9c0cb',
  '#d5dae1',
  '#8a6f3f',
] as const

/**
 * The swatches a partner is offered before reaching for the native picker.
 *
 * These are values a tenant *chooses*, not tokens the product paints with — the same category
 * as a network colour: a colour carrying data. They live here rather than in the panels that
 * show them so the in-app studio and the public `/whitelabel` page offer one palette.
 */
export const PARTNER_CHROME_PRESETS = [
  STOCK.chrome,
  '#101317',
  '#1b2430',
  '#241f1b',
  '#101a17',
] as const

export const PARTNER_ACCENT_PRESETS = [
  STOCK.accent,
  '#c96f2e',
  '#4a6fa5',
  '#2e9e6b',
  '#8a5a9e',
] as const

/** A new brand's ring before anyone picks one: neutral, so it reads as "unset", not as data. */
export const BRAND_RING_DEFAULT: string = STOCK.inkMute

/** Picks the legible ink for a background — the token name, not a hex. */
export const inkOn = (background: string): 'var(--ink)' | 'var(--chrome-ink)' =>
  contrastRatio(background, STOCK.ink) >= contrastRatio(background, STOCK.chromeInk)
    ? 'var(--ink)'
    : 'var(--chrome-ink)'

/**
 * Darkens an accent until it reads at `target` against a paper background.
 * Used for `--brass-deep`, which is the only brass allowed to carry text on light.
 */
export const clampForContrast = (hex: string, against: string, target = 4.5): string => {
  const rgb = parseHex(hex)
  if (!rgb) return hex
  const base = rgbToOklch(rgb)
  let candidate = hex
  for (let l = base.l; l >= 0.1; l -= 0.02) {
    candidate = toHex(oklchToRgb({ l, c: base.c, h: base.h }))
    if (contrastRatio(candidate, against) >= target) return candidate
  }
  return candidate
}

/* ── derivation ───────────────────────────────────────────────────────────── */

/** Lightness deltas measured off the stock chrome ramp (#16191E → #1D212A → #242932 → #333A46). */
const CHROME_STEPS = { c800: 0.037, c700: 0.07, c600: 0.144 } as const

/**
 * Declared as a type alias, not an interface, on purpose: only a type literal gets TypeScript's
 * implicit index signature, which is what lets `Object.entries` see the values as `string`
 * instead of falling back to its `[string, any][]` overload at every call site below.
 */
export type DerivedPalette = {
  '--chrome-900': string
  '--chrome-800': string
  '--chrome-700': string
  '--chrome-600': string
  '--chrome-ink': string
  '--chrome-mute': string
  '--brass': string
  '--brass-deep': string
  '--brass-wash': string
}

/**
 * The whole tenant palette from two inputs. Pure — the white-label panel calls this
 * directly for its live preview without touching the document.
 */
export const derivePalette = (chrome: string, accent: string): DerivedPalette => ({
  '--chrome-900': chrome,
  '--chrome-800': shiftLightness(chrome, CHROME_STEPS.c800),
  '--chrome-700': shiftLightness(chrome, CHROME_STEPS.c700),
  '--chrome-600': shiftLightness(chrome, CHROME_STEPS.c600),
  '--chrome-ink': atLightness(chrome, 0.925, 0.012),
  '--chrome-mute': atLightness(chrome, 0.655, 0.022),
  '--brass': accent,
  '--brass-deep': clampForContrast(accent, STOCK.surface, 4.5),
  '--brass-wash': atLightness(accent, 0.966, 0.03),
})

/* ── the branding store ───────────────────────────────────────────────────── */

export const DEFAULT_BRANDING: PublicBranding = {
  productName: 'Anubis Connector',
  logoLightUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  colorChrome: STOCK.chrome,
  colorAccent: STOCK.accent,
  loginBackgroundUrl: null,
  supportEmail: null,
  hidePoweredBy: false,
}

export type BrandingStatus = 'pending' | 'ready'

let current: PublicBranding = DEFAULT_BRANDING
let status: BrandingStatus = 'pending'
let snapshot: { branding: PublicBranding; status: BrandingStatus } = {
  branding: current,
  status,
}

const subscribers = new Set<() => void>()

const publish = (): void => {
  snapshot = { branding: current, status }
  for (const fn of subscribers) fn()
}

const subscribe = (fn: () => void): (() => void) => {
  subscribers.add(fn)
  return () => {
    subscribers.delete(fn)
  }
}

/** Fires whenever the palette changes so chart themes can re-read `getComputedStyle`. */
export const BRANDING_EVENT = 'anubis-connector:branding'

const isStockPalette = (branding: PublicBranding): boolean =>
  branding.colorChrome.toLowerCase() === STOCK.chrome &&
  branding.colorAccent.toLowerCase() === STOCK.accent

/** Writes the derived palette onto `:root`. Also swaps favicon, title and theme-colour. */
export const applyBranding = (branding: PublicBranding): void => {
  const root = document.documentElement

  if (isStockPalette(branding)) {
    // The stock ramp in tokens.css was tuned by hand; derivation approximates it. When the
    // tenant has not changed the colours, drop every override and let the tokens stand.
    for (const token of Object.keys(derivePalette(STOCK.chrome, STOCK.accent))) {
      root.style.removeProperty(token)
    }
  } else {
    for (const [token, value] of Object.entries(
      derivePalette(branding.colorChrome, branding.colorAccent),
    )) {
      root.style.setProperty(token, value)
    }
  }

  document.title = `${branding.productName} — social analytics & scheduling`

  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (themeColor) themeColor.content = branding.colorChrome

  if (branding.faviconUrl) {
    const icon =
      document.querySelector<HTMLLinkElement>('#app-favicon') ??
      document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (icon) {
      icon.href = branding.faviconUrl
      icon.type = branding.faviconUrl.endsWith('.svg') ? 'image/svg+xml' : 'image/png'
    }
  }

  window.dispatchEvent(new CustomEvent(BRANDING_EVENT))
}

/** Live preview for the white-label panel: applies without becoming the stored branding. */
export const previewPalette = (chrome: string, accent: string): void => {
  const palette = derivePalette(chrome, accent)
  const root = document.documentElement
  for (const [token, value] of Object.entries(palette)) {
    root.style.setProperty(token, value)
  }
  window.dispatchEvent(new CustomEvent(BRANDING_EVENT))
}

/** Drops every inline override, returning the document to the stock tokens. */
export const resetPalette = (): void => {
  const root = document.documentElement
  for (const token of Object.keys(derivePalette(STOCK.chrome, STOCK.accent))) {
    root.style.removeProperty(token)
  }
  window.dispatchEvent(new CustomEvent(BRANDING_EVENT))
}

export const setBranding = (branding: PublicBranding): void => {
  current = branding
  status = 'ready'
  applyBranding(branding)
  publish()
}

let bootPromise: Promise<PublicBranding> | null = null

/**
 * Resolves the branding for the host the SPA was served from and applies it.
 * Unauthenticated by design — it runs before anyone has signed in.
 */
export const bootBranding = (): Promise<PublicBranding> => {
  if (bootPromise) return bootPromise
  bootPromise = (async (): Promise<PublicBranding> => {
    try {
      const resolved = await apiV1<PublicBranding>('/whitelabel/resolve', {
        query: { domain: window.location.host },
        anonymous: true,
      })
      setBranding(resolved)
      return resolved
    } catch {
      // No tenant on this host, or the API is down — the stock palette is already correct.
      status = 'ready'
      publish()
      return DEFAULT_BRANDING
    }
  })()
  return bootPromise
}

const getSnapshot = (): { branding: PublicBranding; status: BrandingStatus } => snapshot

export interface UseBrandingResult {
  branding: PublicBranding
  status: BrandingStatus
  /** True while the tenant lookup is still in flight. */
  resolving: boolean
}

/** Product name, logos and login background for any component that renders chrome. */
export const useBranding = (): UseBrandingResult => {
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return { branding: value.branding, status: value.status, resolving: value.status === 'pending' }
}

/** The logo for a surface, or `null` when the tenant has not uploaded one. */
export const logoFor = (branding: PublicBranding, surface: 'chrome' | 'paper'): string | null =>
  surface === 'chrome'
    ? (branding.logoDarkUrl ?? branding.logoLightUrl)
    : (branding.logoLightUrl ?? branding.logoDarkUrl)
